import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { restaurantConfig } from '../config/restaurantConfig';
import AnimatedHamburger from './AnimatedHamburger';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  logo: string;
  language: string;
  toggleLanguage: () => void;
  handleReservation: () => void;
}

const Drawer: React.FC<DrawerProps> = ({ 
  isOpen, 
  onClose, 
  logo, 
  language, 
  toggleLanguage,
  handleReservation
}) => {
  const flowersRef = useRef<HTMLDivElement>(null);

  // Close drawer when escape key is pressed
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);
  
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Reset flower animations when drawer opens
  useEffect(() => {
    if (isOpen && flowersRef.current) {
      // Reset and restart flower animations
      const flowers = flowersRef.current.querySelectorAll('.drawer-garden-flower');
      flowers.forEach(flower => {
        (flower as HTMLElement).style.animation = 'none';
        // Reset particle animations as well
        const particles = flower.querySelectorAll('.flower__particle');
        particles.forEach(particle => {
          (particle as HTMLElement).style.animation = 'none';
        });
        
        setTimeout(() => {
          if (flower.className.includes('drawer-garden-flower--1')) {
            (flower as HTMLElement).style.animation = 'grow-from-bottom 1.2s forwards, moving-flower-1 4s linear infinite';
          } else if (flower.className.includes('drawer-garden-flower--2')) {
            (flower as HTMLElement).style.animation = 'grow-from-bottom 1.2s forwards, moving-flower-2 4s linear infinite';
          } else if (flower.className.includes('drawer-garden-flower--3')) {
            (flower as HTMLElement).style.animation = 'grow-from-bottom 1.2s forwards, moving-flower-3 4s linear infinite';
          }
          
          // Restart particle animations
          particles.forEach((particle, index) => {
            const delay = 0.3 + (index * 0.2);
            (particle as HTMLElement).style.animation = `particle-float 3s ${delay}s ease-in-out infinite`;
          });
        }, 50);
      });
    }
  }, [isOpen]);

  const scrollToBookingSection = () => {
    onClose(); // Close the drawer first
    
    // Check if on homepage
    if (window.location.pathname !== '/') {
      // Navigate to homepage first, then scroll to booking section
      window.location.href = '/#booking';
    } else {
      // On homepage, just scroll to booking section
      const bookingSection = document.getElementById('booking');
      if (bookingSection) {
        bookingSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLinkClick = () => {
    onClose();
  };

  return (
    <>
      {/* Backdrop Overlay - Clickable to dismiss drawer */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 z-40 transition-opacity duration-300"
          onClick={onClose}
          aria-hidden="true"
        ></div>
      )}
      
      {/* Drawer / Sidebar Panel */}
      <section className={`hidden-bar ${isOpen ? "visible-sidebar" : ""}`}>
        <div className="inner-box">
          {/* Logo */}
          <div className="logo-box pt-0">
            <Link 
              to="/" 
              onClick={() => {
                handleLinkClick();
                // If already on home page, scroll to top
                if (window.location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex justify-center"
            >
              <img 
                src={logo} 
                alt="Restaurant Logo" 
                className="h-24 object-contain transform scale-150"
              />
            </Link>
          </div>

          {/* Navigation Menu */}
          <div className="side-menu">
            <ul className="navigation clearfix">
              <li>
                <Link to="/" onClick={handleLinkClick}>
                  {language === 'en' ? 'HOME' : 'INÍCIO'}
                </Link>
              </li>
              <li>
                <a 
                  href="#about" 
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    const aboutSection = document.getElementById('about');
                    if (aboutSection) {
                      aboutSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  {language === 'en' ? 'OUR STORY' : 'A NOSSA HISTÓRIA'}
                </a>
              </li>
              <li>
                <a 
                  href="#gallery" 
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    
                    // Check if on homepage
                    if (window.location.pathname !== '/') {
                      // Navigate to homepage first, then scroll to gallery
                      window.location.href = '/#gallery';
                    } else {
                      // On homepage, just scroll to gallery
                      const gallerySection = document.getElementById('gallery');
                      if (gallerySection) {
                        gallerySection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }}
                >
                  {language === 'en' ? 'GALLERY' : 'GALERIA'}
                </a>
              </li>
              <li>
                <a 
                  href="#contact" 
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    
                    // Check if on homepage
                    if (window.location.pathname !== '/') {
                      // Navigate to homepage first, then scroll to contact section
                      window.location.href = '/#contact';
                    } else {
                      // On homepage, just scroll to contact section
                      const contactSection = document.getElementById('contact');
                      if (contactSection) {
                        contactSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }}
                >
                  {language === 'en' ? 'CONTACT' : 'CONTATO'}
                </a>
              </li>
              <li>
                <a 
                  href="#menu" 
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    
                    // Check if on homepage
                    if (window.location.pathname !== '/') {
                      // Navigate to homepage first, then scroll to menu
                      window.location.href = '/#menu';
                    } else {
                      // On homepage, just scroll to menu
                      const menuSection = document.getElementById('menu');
                      if (menuSection) {
                        menuSection.scrollIntoView({ behavior: 'smooth' });
                      }
                    }
                  }}
                >
                  {language === 'en' ? 'MENU' : 'MENU'}
                </a>
              </li>
              
              {/* Language Switcher */}
              <li className="language-switch">
                <div className="language-btns flex mt-2 mb-4">
                  <button 
                    onClick={toggleLanguage}
                    className={`language-flag-btn flex items-center gap-2 ${language === 'pt' ? 'opacity-100' : 'opacity-60'} mr-4 hover:opacity-100 transition-opacity duration-300`}
                    aria-label="Switch to Portuguese"
                  >
                    <div className={`w-8 h-8 rounded-full overflow-hidden border-2 flex-shrink-0 ${language === 'pt' ? 'border-pink-500' : 'border-transparent'}`}>
                      <img 
                        src="/images/svg/Portugal.svg" 
                        alt="Portuguese Flag"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span>Português</span>
                  </button>
                  <button 
                    onClick={toggleLanguage}
                    className={`language-flag-btn flex items-center gap-2 ${language === 'en' ? 'opacity-100' : 'opacity-60'} hover:opacity-100 transition-opacity duration-300`}
                    aria-label="Switch to English"
                  >
                    <div className={`w-8 h-8 rounded-full overflow-hidden border-2 flex-shrink-0 ${language === 'en' ? 'border-pink-500' : 'border-transparent'}`}>
                      <img 
                        src="/images/svg/Flag_of_Uk.svg" 
                        alt="English Flag"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span>English</span>
                  </button>
                </div>
              </li>
            </ul>
          </div>

          {/* Info Section */}
          <h2>{language === 'en' ? 'Visit Us' : 'Visite-nos'}</h2>
          <ul className="info">
            <li>{language === 'en' ? restaurantConfig.address.en : restaurantConfig.address.pt}</li>
            <li>
              {language === 'en' 
                ? restaurantConfig.businessHours.en.join(" • ") 
                : restaurantConfig.businessHours.pt.join(" • ")}
            </li>
            <li><Link to={`mailto:${restaurantConfig.email}`}>{restaurantConfig.email}</Link></li>
          </ul>
          
          {/* Booking Information */}
          <div className="booking-info">
            <div className="bk-title">
              {language === 'en' ? 'Booking request' : 'Reserva'}
            </div>
            <div className="bk-no">
              <Link to={`tel:${restaurantConfig.phone}`}>{restaurantConfig.phone}</Link>
            </div>
            <a 
              href="tel:+351282763777"
              className="bg-[#cb6e92] text-white w-full py-3 rounded-full transition-all duration-300 hover:bg-opacity-90 hover:shadow-lg hover:-translate-y-1 font-medium mt-4 inline-block text-center"
            >
              {language === 'en' ? 'Book a Table' : 'Reservar Mesa'}
            </a>
          </div>
          
          {/* Garden Layer with Flowers */}
          <div className="drawer-garden-layer">
            <div className="drawer-flowers-container" ref={flowersRef}>
              {/* Generate flowers with evenly distributed positions */}
              {[...Array(15)].map((_, i) => {
                // Calculate position to evenly distribute flowers
                const position = (i / 14) * 100; // 14 = 15-1, to get proper distribution from 0% to 100%
                
                return (
                <div 
                  key={`drawer-flower-${i}`} 
                  className={`drawer-garden-flower drawer-garden-flower--${i % 3 + 1} ${isOpen ? 'visible' : 'hidden'}`} 
                  style={{
                    left: `${position}%`,
                    bottom: '0px',
                    animationDelay: `${0.1 + Math.random() * 0.7}s`,
                    zIndex: Math.floor(Math.random() * 5) + 5
                  }}
                >
                  <div className="flower__leafs flower__leafs--1">
                    <div className="flower__leaf flower__leaf--1"></div>
                    <div className="flower__leaf flower__leaf--2"></div>
                    <div className="flower__leaf flower__leaf--3"></div>
                    <div className="flower__leaf flower__leaf--4"></div>
                    <div className="flower__white-circle"></div>
                    
                    <div className="flower__particle flower__particle--1"></div>
                    <div className="flower__particle flower__particle--2"></div>
                    <div className="flower__particle flower__particle--3"></div>
                  </div>
                  <div className="flower__line">
                    <div className="flower__line__leaf flower__line__leaf--1"></div>
                    <div className="flower__line__leaf flower__line__leaf--2"></div>
                    <div className="flower__line__leaf flower__line__leaf--3"></div>
                    <div className="flower__line__leaf flower__line__leaf--4"></div>
                  </div>
                </div>
              )})}
            </div>
          </div>
        </div>
      </section>

      {/* Custom style to ensure backdrop visibility and proper dimming */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .hidden-bar .inner-box {
            padding-top: 40px;
            padding-bottom: 60px; /* Add padding to make room for flowers */
            position: relative; /* For absolute positioning of flowers */
          }
          
          /* Move menu items more upwards */
          .hidden-bar .side-menu {
            padding-top: 30px;
            padding-bottom: 10px;
          }
          
          /* Reduce space above logo and increase space below */
          .hidden-bar .logo-box {
            padding-top: 0;
            padding-bottom: 30px;
            margin-top: -10px;
            display: flex;
            justify-content: center;
          }
          
          /* Make sidebar appear above the overlay */
          .hidden-bar.visible-sidebar {
            z-index: 50;
          }

          /* Drawer Garden Styles */
          .drawer-garden-layer {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 60px;
            overflow: hidden;
          }
          
          .drawer-flowers-container {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 100%;
          }
          
          .drawer-garden-flower {
            position: absolute;
            transform-origin: bottom center;
            --fl-speed: 0.8s;
            transform: scale(0.6); /* Make flowers smaller in drawer */
            transition: opacity 0.8s ease-out, transform 0.8s ease-out;
          }
          
          .drawer-garden-flower.hidden {
            transform: translateY(100%) scale(0.6);
            opacity: 0;
          }
          
          .drawer-garden-flower.visible {
            opacity: 1;
          }
          
          .drawer-garden-flower--1 {
            animation: none; /* Initially no animation */
          }
          
          .drawer-garden-flower--2 {
            animation: none; /* Initially no animation */
          }
          
          .drawer-garden-flower--3 {
            animation: none; /* Initially no animation */
          }
          
          @keyframes grow-from-bottom {
            0% {
              transform: translateY(100%) scale(0.6);
              opacity: 0;
            }
            100% {
              transform: translateY(0) scale(0.6);
              opacity: 1;
            }
          }
          
          @keyframes moving-flower-1 {
            0%, 100% {
              transform: translateY(0) rotate(5deg) scale(0.6);
            }
            50% {
              transform: translateY(0) rotate(-5deg) scale(0.6);
            }
          }
          
          @keyframes moving-flower-2 {
            0%, 100% {
              transform: translateY(0) rotate(20deg) scale(0.6);
            }
            50% {
              transform: translateY(0) rotate(12deg) scale(0.6);
            }
          }
          
          @keyframes moving-flower-3 {
            0%, 100% {
              transform: translateY(0) rotate(-20deg) scale(0.6);
            }
            50% {
              transform: translateY(0) rotate(-25deg) rotateY(-15deg) scale(0.6);
            }
          }
        `
      }} />
    </>
  );
};

export default Drawer; 